import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// MODULES
import { DropdownModule } from './dropdown/dropdown.module';
import { ButtonModule } from './button/button.module';
import { FormModule } from './form/form.module';
import { NavModule } from './nav/nav.module';
import { IconModule } from './icon/icon.module';
import { PaginationModule } from './pagination/pagination.module';
import { AppModalModule } from './modal/modal.module';
import { ProductSharedModule } from './product/product-shared.module';
import { HintsModule } from './hints/hints.module';
import { LogsModule } from './logs/logs.module';
import { SortableModule } from './sortable/sortable.module';
import { TableModule } from './table/table.module';
import { CollapseModule } from './collapse/collapse.module';
import { ScrollModule } from './scroll/scroll.module';
import { PipesModule } from './pipes/pipes.module';
import { ValidationModule } from './validation/validation.module';
import { ToasterModule } from './toaster/toaster.module';
import { SearchModule } from './search/search.module';
import { AuthModule } from './auth/auth.module';
import { ImageRendererModule } from './image-renderer/image-renderer.module';
import { LoaderModule } from './loader/loader.module';
import { EmptyStateModule } from './empty-state/empty-state.module';
import { AppTooltipModule } from './tooltip/tooltip.module';
import { SpinnerModule } from './spinner/spinner.module';
import { ColumnBuilderModule } from './column-builder/column-builder.module';
import { UserAvatarModule } from './user-avatar/user-avatar.module';
import { FitmentsModule } from './fitments/fitments.module';
import { StatusFieldModule } from './status-field/status-field.module';
import { CommentsModule } from './comments/comments.module';
import { ContextualActionsModule } from './contextual-actions/contextual-actions.module';
import { VersionsModule } from './versions/versions.module';
import { UploadAssetsModule } from './digital-assets/upload-assets/upload-assets.module';
import { DetailsPageModule } from './details-page/details-page.module';
import { PreloaderModule } from './preloader/preloader.module';
import { SideDrawerModule } from './sidedrawer/sidedrawer.module';
import { DownloadItemModule } from './download-item/download-item.module';
import { ShareLinkModule } from './share-link/share-link.module';
import { TermsModule } from './terms/terms.module';
import { SubscriptionWidgetModule } from './subscription/subscription-widget/subscription-widget.module';
import { OnlyIntegerNumberModule } from './only-integer-number/only-integer-number.module';
import { LinkModule } from './link/link.module';
import { StatusSideNavModule } from './status-sidenav/status-sidenav.module';
import { SegmentsNavigationModule } from './segments-navigation/segments-navigation.module';
import { RightPanelModule } from './right-panel/right-panel.module';
import { AccountModule } from './account/account.module';
import { RadialProgressModule } from './radial-progress/radial-progress.module';
import { BadgeModule } from './badge/badge.module';
import { TabsModule } from './tabs/tabs.module';
import { SearchPageModule } from './search-page/search-page.module';
import { AppHeaderModule } from './header/header.module';
import { ReceiversSharedModule } from './receivers/receivers-shared.module';
import { AlertsModule } from 'shared/alerts/alerts.module';

// COMPONENTS
import { BaseDropdownComponent } from './dropdown/_base-dropdown.component';
import { NavItemBaseComponent } from './nav/nav-item/_nav-item-base.component';
import { SearchWidgetComponent } from './search-widget/search-widget.component';
import { FrontendSearchWidgetComponent } from './search-widget/frontend-search-widget.component';
import { TableComponent } from './table/table/table.component';
import { BaseRoutingPageComponent } from './base/_base-routing-page.component';
import { CurrentTasksComponent } from './account/current-tasks/current-tasks.component';
import { ActionPanelComponent } from './action-panel/action-panel.component';
import { PagesDropdownMenuComponent } from './pages-dropdown-menu/pages-dropdown-menu.component';
import { StickyComponent } from './sticky/sticky.component';
import { LogoHeaderComponent } from './logo-header/logo-header.component';
import { MobileNavMenuComponent } from './mobile-nav-menu/mobile-nav-menu.component';
import { ListManagerComponent } from './list-manager/list-manager.component';
import { AddUrlModalComponent } from 'shared/add-url-modal/add-url-modal.component';
import { MarketingFormComponent } from 'shared/marketing-form/marketing-form.component';
import { AssetDescriptionsComponent } from 'shared/asset-descriptions/asset-descriptions.component';

// DIRECTIVES
import { BaseDirective } from './base/_base.directive';


// SERVICES
// import { AuthService } from './auth/auth.service';
// import { AuthGuard } from './auth/auth.guard';;
import { StickyService } from './sticky/sticky.service';
import { BrandService } from './brand/brand.service';
import { BrandInfoService } from './brand/brand-info.service';
import { UserService } from './user/user.service';
import { ProcessService } from './process/process.service';
import { ExportManagerService } from './export-manager/export-manager.service';
import { ImportManagerService } from './import-manager/import-manager.service';
import { ItemDetailService } from './item-detail/item-detail.service';
import { PriceSheetService } from './price-sheet/price-sheet.service';
import { LookupsService } from './lookups/lookups.service';
import { MarketingCopyService } from './marketing-copy/marketing-copy.service';
import { MarketingCopyDetailsService } from './marketing-copy/marketing-copy-details.service';
import { ProductGroupsService } from './product-groups/product-groups.service';
import { CoreService } from './global-core/core.service';
import { GlobalValidationService } from './global-validation/global-validation.service';
import { TradeInfoService } from './trade-info/trade-info.service';
import { SessionService } from './session/session.service';
import { MyProductsStoreDataService } from './my-products-store-data/my-products-store-data.service';
import { DigitalAssetsService } from './digital-assets/digital-assets.service';
import { PartTypesSearchService } from './part-types/search/part-types-search.service';
import { VehicleService } from './vehicle/vehicle.service';
import { GoogleAnalyticsEventsService } from './google-analytics/google-analytics-events.service';
import { ProfileInfoService } from './profile/profile-info.service';
import { TeamService } from './team/team.service';
import { PreferencesService } from './preferences/preferences.service';
import { InviteUserService } from './invite-user/invite-user.service';
import { InviteUserUnguardedService } from './invite-user/invite-user-unguarded.service';
import { PermissionsService } from './permissions/permissions.service';
import { SubscriptionService } from './subscription/subscription.service';
import { SubscriptionGuard } from './subscription/subscription.guard';
import { TermsService } from './terms/terms.service';
import { BillingService } from './billing/billing.service';
import { ReturnRouteService } from './return-route/return-route.service';
import { SavedSearchesService } from './saved-searches/saved-searches.service';
import { ReportsService } from './reports/reports.service';
import { PartNumberService } from './part-number/part-number.service';
import { AlertsService } from './alerts/alerts.service';
import { PermissionsGuard } from './permissions/permissions.guard';
import { ProductsPageGuard } from './product/products-page.guard';
import { ExportManagerSearchService } from './export-manager/export-manager-search.service';
import { AppSummaryService } from './app-summary/app-summary.service';
import {
  CurrentProductsSearchService
} from 'shared/modal/create-product-copy-modal/current-products-search/current-products-search.service';
import { UpdateService } from 'shared/update/update.service';
import { CheckForUpdateService } from 'shared/update/check-for-update.service';
import { PusherService } from 'shared/pusher/pusher.service';

// RESOLVERS
import { PermissionsResolver } from './permissions/permissions.resolver';
import { PreferencesResolver } from './preferences/preferences.resolver';
import { ScorecardsResolver } from './scorecard/scorecards.resolver';

/**
 * Do not specify providers for modules that might be imported by a lazy loaded module.
 */

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        DropdownModule,
        ButtonModule,
        FormModule,
        ReactiveFormsModule,
        FormsModule,
        NavModule,
        IconModule,
        PaginationModule,
        AppModalModule,
        ProductSharedModule,
        HintsModule,
        LogsModule,
        SortableModule,
        TableModule,
        CollapseModule,
        ScrollModule,
        PipesModule,
        ValidationModule,
        ToasterModule,
        SearchModule,
        AuthModule,
        ImageRendererModule,
        LoaderModule,
        EmptyStateModule,
        AppTooltipModule,
        SpinnerModule,
        ColumnBuilderModule,
        UserAvatarModule,
        FitmentsModule,
        StatusFieldModule,
        CommentsModule,
        ContextualActionsModule,
        TermsModule,
        SubscriptionWidgetModule,
        OnlyIntegerNumberModule,
        VersionsModule,
        DownloadItemModule,
        ShareLinkModule,
        PreloaderModule,
        LinkModule,
        UploadAssetsModule,
        SideDrawerModule,
        StatusSideNavModule,
        SegmentsNavigationModule,
        DetailsPageModule,
        RightPanelModule,
        BadgeModule,
        AccountModule,
        RadialProgressModule,
        TabsModule,
        SearchPageModule,
        AppHeaderModule,
        ReceiversSharedModule,
        AlertsModule,
    ],
    declarations: [
        BaseDropdownComponent,
        NavItemBaseComponent,
        SearchWidgetComponent,
        FrontendSearchWidgetComponent,
        TableComponent,
        BaseRoutingPageComponent,
        ActionPanelComponent,
        PagesDropdownMenuComponent,
        ListManagerComponent,
        StickyComponent,
        LogoHeaderComponent,
        MobileNavMenuComponent,
        BaseDirective,
        AddUrlModalComponent,
        MarketingFormComponent,
        AssetDescriptionsComponent,
    ],
    exports: [
        CommonModule,
        RouterModule,
        DropdownModule,
        ButtonModule,
        FormModule,
        NavModule,
        IconModule,
        PaginationModule,
        AppModalModule,
        ProductSharedModule,
        HintsModule,
        SortableModule,
        TableModule,
        CollapseModule,
        ScrollModule,
        PipesModule,
        ValidationModule,
        ToasterModule,
        SearchModule,
        AuthModule,
        ImageRendererModule,
        LoaderModule,
        EmptyStateModule,
        AppTooltipModule,
        SpinnerModule,
        ColumnBuilderModule,
        UserAvatarModule,
        FitmentsModule,
        StatusFieldModule,
        CommentsModule,
        ContextualActionsModule,
        TermsModule,
        SubscriptionWidgetModule,
        OnlyIntegerNumberModule,
        VersionsModule,
        DownloadItemModule,
        ShareLinkModule,
        PreloaderModule,
        LinkModule,
        UploadAssetsModule,
        SideDrawerModule,
        StatusSideNavModule,
        SegmentsNavigationModule,
        DetailsPageModule,
        RightPanelModule,
        BadgeModule,
        AccountModule,
        RadialProgressModule,
        LogsModule,
        TabsModule,
        SearchPageModule,
        AppHeaderModule,
        ReceiversSharedModule,
        AlertsModule,
        BaseDropdownComponent,
        NavItemBaseComponent,
        SearchWidgetComponent,
        FrontendSearchWidgetComponent,
        TableComponent,
        BaseRoutingPageComponent,
        ActionPanelComponent,
        PagesDropdownMenuComponent,
        ListManagerComponent,
        StickyComponent,
        LogoHeaderComponent,
        MobileNavMenuComponent,
        BaseDirective,
        AddUrlModalComponent,
        MarketingFormComponent,
        AssetDescriptionsComponent,
    ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<any> {
    return {
      ngModule: SharedModule,
      providers: [
        // AuthService,
        // AuthGuard,
        BrandService,
        BrandInfoService,
        UserService,
        ProcessService,
        StickyService,
        ExportManagerService,
        ImportManagerService,
        ItemDetailService,
        PriceSheetService,
        LookupsService,
        MarketingCopyService,
        MarketingCopyDetailsService,
        ProductGroupsService,
        CoreService,
        GlobalValidationService,
        TradeInfoService,
        SessionService,
        MyProductsStoreDataService,
        DigitalAssetsService,
        PartTypesSearchService,
        VehicleService,
        GoogleAnalyticsEventsService,
        ProfileInfoService,
        TeamService,
        PreferencesService,
        InviteUserService,
        InviteUserUnguardedService,
        PermissionsService,
        BillingService,
        ReturnRouteService,
        SavedSearchesService,
        SubscriptionService,
        ReportsService,
        TermsService,
        PartNumberService,
        AlertsService,
        ExportManagerSearchService,
        AlertsService,
        AppSummaryService,
        CurrentProductsSearchService,
        UpdateService,
        CheckForUpdateService,
        PusherService,
        PermissionsResolver,
        PreferencesResolver,
        ScorecardsResolver,
        SubscriptionGuard,
        PermissionsGuard,
        ProductsPageGuard,
      ]
    };
  }
}
